import { FC } from "react";
import styles from "./CatalogFilterModal.module.scss";
import { PriceControls } from "@/lib/ui/components/Selects/DropDownPrice";
import { P2 } from "@/lib/ui/components/Typography";
import { useTranslation } from "next-i18next";
import cl from "classnames";
import { CatalogCheckboxBlock } from "../CatalogFilter/CatalogCheckboxBlock";
import {
  bathroomOptions,
  beachDistanceOptions,
  phuketBeachesOptions,
  bedroomsOptions,
  constructionOptions,
  facilitiesOptions,
  investOptions,
  landscapeOptions,
  ownershipTypeOptions,
  propertyTypeOptions,
  viewOptions,
  samuiBeachesOptions,
} from "../CatalogFilter/constants";
import { Button } from "@/lib/ui/components/Buttons";
import { SvgPerfectInvest } from "@/lib/ui/icons/catalog-filter";
import { useCatalogFilter } from "@/lib/hooks/useCatalogFilter";
import { getWordEnding } from "@/lib/utils/getWordEnding";
import { modal } from "@/lib/ui/components/Modal";
import { OptionsBlock } from "./OptionsBlock";
import { CatalogMap } from "../CatalogMap";
import { MapPinIcon } from "@/lib/ui/icons/MapPinIcon";
import { useAppSelector } from "@/lib/store/app";
import { getFilteredProperties, useTranslateAndDetectDisabled } from "@/lib/hooks/useFilteredProperties";

export const CatalogFilterModal: FC = () => {
  const prices = useAppSelector((state) => state.catalog.prices);
  const quantity = useAppSelector((state) => state.catalog.filteredQuantity);
  const properties = useAppSelector((state) => state.catalog.properties);
  const [min, max] = prices;
  const {
    t,
    i18n: { language },
  } = useTranslation("index");
  const { value: currency, symbol } = useAppSelector((state) => state.settings.currency);

  const { toggleParam, query, resetParams } = useCatalogFilter();
  const translateAndDetectDisabled = useTranslateAndDetectDisabled();

  return (
    <div className={styles.modal}>
      <div className={styles.modal__top}>
        <P2>{t("allFilter")}</P2>
      </div>
      <div className={styles.modal__content}>
        <div className={styles.modal__contentLeft}>
          <OptionsBlock
            items={translateAndDetectDisabled(propertyTypeOptions, "propertyType")}
            catalogQuery="propertyType"
          />
          <div
            className={cl(
              styles.modal__filterBlock,
              styles["modal__filterBlock--district"],
            )}
          >
            <Button
              label={t("islandAndDistrict")}
              theme="dark-invert"
              size="lg"
              as="button"
              className={styles.modal__district}
              icon={<MapPinIcon />}
              onClick={() => {
                modal.open(<CatalogMap includeSelectAll />, {
                  isMapModal: true,
                  hideCloseButton: true,
                });
              }}
            />
          </div>
          <div className={styles.modal__filterBlock}>
            <P2 className={styles.modal__filterTitle}>{t("cost")}</P2>
            <PriceControls
              currency={currency}
              symbol={symbol}
              max={max}
              min={min}
              isInModal
            />
          </div>
          <div className={cl(styles.modal__filterBlock)}>
            <P2 className={styles.modal__filterTitle}>{t("bedrooms")}</P2>
            <CatalogCheckboxBlock
              isButton
              items={
                translateAndDetectDisabled(
                  bedroomsOptions,
                  "bedrooms",
                )
              }
              catalogQuery="bedrooms"
            />
          </div>
          <div className={cl(styles.modal__filterBlock)}>
            <P2 className={styles.modal__filterTitle}>{t("catalog.bathroomAmount")}</P2>
            <CatalogCheckboxBlock
              isButton
              items={
                translateAndDetectDisabled(
                  bathroomOptions,
                  "bathroomAmount",
                )
              }
              catalogQuery="bathroomAmount"
            />
          </div>
          <OptionsBlock
            items={translateAndDetectDisabled(beachDistanceOptions, "beachDistance")}
            catalogQuery="beachDistance"
          />
          <OptionsBlock
            items={translateAndDetectDisabled(investOptions, "investAttractiveness")}
            catalogQuery="investAttractiveness"
          >
            <Button
              theme="grayBorder"
              size="md"
              label={t("perfectForInvest")}
              as="button"
              icon={<SvgPerfectInvest />}
              isIconLeft
              disabled={
                getFilteredProperties(properties, { perfectInvest: "true" }, currency)
                  .length === 0
              }
              className={cl(
                styles.modal__button,
                query.perfectInvest === "true" && styles["modal__button--active"],
              )}
              onClick={() => toggleParam("perfectInvest")}
            />
          </OptionsBlock>
          <OptionsBlock
            items={translateAndDetectDisabled(constructionOptions, "buildingStages")}
            catalogQuery="buildingStages"
          />
          <OptionsBlock
            items={translateAndDetectDisabled(viewOptions, "view")}
            catalogQuery="view"
          />
          <OptionsBlock
            items={translateAndDetectDisabled(landscapeOptions, "landscape")}
            catalogQuery="landscape"
          />
          <OptionsBlock
            items={translateAndDetectDisabled(ownershipTypeOptions, "ownershipType")}
            catalogQuery="ownershipType"
          />
          <Button
            theme="grayBorder"
            size="lg"
            label={t("videoReview")}
            as="button"
            disabled={
              getFilteredProperties(properties, { ...query, videoReview: "true" }, currency)
                .length === 0
            }
            className={cl(
              styles.modal__button,
              query.videoReview === "true" && styles["modal__button--active"],
            )}
            onClick={() => toggleParam("videoReview")}
          />
        </div>
        <div className={styles.modal__contentRight}>
          <CatalogMap isInAllFilters />
          <div className={styles.modal__beaches}>
            <OptionsBlock
              items={translateAndDetectDisabled(phuketBeachesOptions, 'phuketBeaches')}
              catalogQuery="phuketBeaches"
              title={t("phuketBeaches")}
            />
            <OptionsBlock
              items={translateAndDetectDisabled(samuiBeachesOptions, 'samuiBeaches')}
              catalogQuery="samuiBeaches"
              title={t("samuiBeaches")}
            />
          </div>
          <OptionsBlock items={translateAndDetectDisabled(facilitiesOptions, 'facilities')} catalogQuery="facilities" />
        </div>
      </div>
      <div className={styles.modal__bottom}>
        <Button
          as="button"
          size="md"
          theme="dark"
          disabled={quantity === 0}
          label={`${t("show")} ${quantity} ${t(
            getWordEnding(
              quantity,
              ["declinable.offer", "declinable.offers", "declinable.offersMore"],
              language as "ru" | "en",
            ),
          )}`}
          onClick={() => modal.close()}
        />
        <Button
          as="button"
          size="md"
          theme="secondary"
          label={t("reset")}
          onClick={resetParams}
        />
      </div>
    </div>
  );
};
